import { GNEventLog } from "../analyticsUser/types";

// sankey data interface
export interface SankeyDataLink {
  source:string,
  target:string,
  value:number
}
  
export interface SankeyDataNode {
  name: string,
  value: number,
  // depth: number
  layer: number
}
  
export interface SankeyDataInterface {
  links:SankeyDataLink[], 
  nodes:SankeyDataNode[],
  highestValue: number;
  totalValue: number;
}
// BE data interfaces
export interface GNLocationFlowData {
  from: string,
  to: string,
  weight: number
}

export interface GNAICrashOccurrence {
    projectID: string;
    crashGroupID: string;
    clientType: 'client' | 'server' | 'editor' | string;
    eventTimestamp: number;
    eventLocation: string;
    userID: string;
    userName: string;
    pluginVersion: string;
    projectVersion: string;
    platformOS: string;
    platformCPU: string;
    platformGPU: string;
    platformRAM: string;
    downloadURL?: string;
}

export interface GNAICrashGroupItem {
    crashGroupID: string;
    aiSummary: string;
    userSummary: string;
    occurrenceCount: number;
    firstSeenTimestamp: number;
    lastSeenTimestamp: number;
    exampleEventLog?: GNEventLog;
    recentOccurrences: GNAICrashOccurrence[];
}

export interface GNAnalyticsProjectClientDailyStatsItem {
  day: number,
  busySessionHours: number[],
  uniqueUsers: number,
  sessions: number,
  sessionAvgDuration: number,
  crashes: number,
  ensures: number,
  locationVisits: Record<string, number>,
  locationFlows: GNLocationFlowData[]
}

export interface GNAnalyticsProjectClientTypeItem {
  latestUniqueUsers: {
    userID: string,
    userName: string,
    country: string,
    latestSessionTimestamp: number,
    isOnline?: boolean,
    isInEditor?: boolean
  }[],
  geo: Record<string, number>, // country: amount of unique users
  platformOS: Record<string, number>, // platform: amount of unique users
  platformCPU: Record<string, number>, // platform: amount of unique users
  platformGPU: Record<string, number>, // platform: amount of unique users
  platformRAM: Record<string, number>, // platform: amount of unique users
  dailyStats: GNAnalyticsProjectClientDailyStatsItem[],
  crashGroups:GNAICrashGroupItem[]
}

export interface GNAnalyticsReportDownload {
  reportAction: string,
  reportTimestamp: number,
  reportUserID: string,
  reportUserName: string,
  reportClientType: 'client' | 'server' | 'editor' | string,
  reportLocation: string,
  reportProjectVersion: string,
  reportDownloadURL: string,
  reportIsUnderGNAIProcessing: boolean
}

export interface GNProjectSymbolicFilesStatsAPIResponseItem {
    addedTimestamp: number,
    buildID: string,
    platform: string,
    buildType: string,
    fileSize: number,
    isArchived: boolean
}

export interface GNProjectSymbolicFilesStatsAPIResponse {
    activeMapFiles: GNProjectSymbolicFilesStatsAPIResponseItem[],
    archivedMapFiles: GNProjectSymbolicFilesStatsAPIResponseItem[]
}

export interface GNAnalyticsProjectData {
  projectID: string,
  editor: GNAnalyticsProjectClientTypeItem,
  server: GNAnalyticsProjectClientTypeItem,
  client: GNAnalyticsProjectClientTypeItem,
  reportDownloads: GNAnalyticsReportDownload[]
  crashGroupsOccurrenceSinceTimestamp: number;
  crashGroupsProcessiongReportsCount: number;
  lastSymbolicFileUploadTimestamp: number;
  symbolicFilesStats: GNProjectSymbolicFilesStatsAPIResponseItem[];
}

/**
 * Defines Analyticd Project State interface
 */
export interface AnalyticsProjectState {
    // http state
    httpInProgress: boolean;
    httpError: string;
    // symbolic files stats http state
    httpSymbolicFilesStatsInProgress: boolean;
    httpSymbolicFilesStatsError: string;
    // crash group http state
    httpCrashGroupInProgress: boolean;
    httpCrashGroupError: string;
    // crash group update http state
    httpCrashGroupUpdateInProgress: boolean;
    httpCrashGroupUpdateError: string;
    // BE data
    projectData: GNAnalyticsProjectData;
  }
  
  /**
   * Defines Analytics Project actions names
   */
  export const ANALYTICS_PROJECT_IN_PROGRESS = 'ANALYTICS_PROJECT_IN_PROGRESS';
  export const ANALYTICS_PROJECT_ERROR = 'ANALYTICS_PROJECT_ERROR';
  export const ANALYTICS_PROJECT_GET = 'ANALYTICS_PROJECT_GET';
  export const ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_GET = 'ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_GET';
  export const ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_IN_PROGRESS = 'ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_IN_PROGRESS';
  export const ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_ERROR = 'ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_ERROR';
  export const ANALYTICS_CRASH_GROUP_IN_PROGRESS = 'ANALYTICS_CRASH_GROUP_GET_IN_PROGRESS';
  export const ANALYTICS_CRASH_GROUP_ERROR = 'ANALYTICS_CRASH_GROUP_GET_ERROR';
  export const ANALYTICS_CRASH_GROUP_GET = 'ANALYTICS_CRASH_GROUP_GET';
  export const ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS = 'ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS';
  export const ANALYTICS_CRASH_GROUP_UPDATE_ERROR = 'ANALYTICS_CRASH_GROUP_UPDATE_ERROR';
  export const ANALYTICS_CRASH_GROUP_UPDATE = 'ANALYTICS_CRASH_GROUP_UPDATE';
  
  /**
   * Defines Analytics Project actions interfaces
   */
  interface AnalyticsProjectInProgressAction {
    type: typeof ANALYTICS_PROJECT_IN_PROGRESS;
  }
  interface AnalyticsProjectErrorAction {
    type: typeof ANALYTICS_PROJECT_ERROR;
    error: string;
  }
  interface AnalyticsProjectGetAction {
    type: typeof ANALYTICS_PROJECT_GET;
    projectData: GNAnalyticsProjectData;
  }
  interface AnalyticsProjectSymbolicFilesStatsGetAction {
    type: typeof ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_GET;
    symbolicFilesStats: GNProjectSymbolicFilesStatsAPIResponseItem[]
  }
  interface AnalyticsProjectSymbolicFilesStatsInProgressAction {
    type: typeof ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_IN_PROGRESS;
  }
  interface AnalyticsProjectSymbolicFilesStatsErrorAction {
    type: typeof ANALYTICS_PROJECT_SYMBOLIC_FILES_STATS_ERROR;
    error: string;
  }
  interface AnalyticsCrashGroupInProgressAction {
    type: typeof ANALYTICS_CRASH_GROUP_IN_PROGRESS;
  }
  interface AnalyticsCrashGroupErrorAction {
    type: typeof ANALYTICS_CRASH_GROUP_ERROR;
    error: string;
  }
  interface AnalyticsCrashGroupGetAction {
    type: typeof ANALYTICS_CRASH_GROUP_GET;
    projectData: GNAnalyticsProjectData;
  }
  interface AnalyticsCrashGroupUpdateInProgressAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS;
  }
  interface AnalyticsCrashGroupUpdateErrorAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE_ERROR;
    error: string;
  }
  interface AnalyticsCrashGroupUpdateAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE;
    projectData: GNAnalyticsProjectData;
  }
  
  /**
   * Defines all suported actions for Analytics Project
   */
  export type AnalyticsProjectActionTypes =
    AnalyticsProjectInProgressAction |
    AnalyticsProjectErrorAction |
    AnalyticsProjectGetAction |
    AnalyticsProjectSymbolicFilesStatsGetAction |
    AnalyticsProjectSymbolicFilesStatsInProgressAction |
    AnalyticsProjectSymbolicFilesStatsErrorAction |
    AnalyticsCrashGroupInProgressAction |
    AnalyticsCrashGroupErrorAction |
    AnalyticsCrashGroupGetAction |
    AnalyticsCrashGroupUpdateInProgressAction |
    AnalyticsCrashGroupUpdateErrorAction |
    AnalyticsCrashGroupUpdateAction;
  
  /**
   * Defines AnalyticsProjectActionsInterface
   */
  export interface AnalyticsProjectActionsInterface {
    analyticsProjectGet: (projectID: string) => Promise<void>;
    analyticsProjectSymbolicFilesStatsGet: (projectID: string) => Promise<void>;
    analyticsCrashGroupGet: ( projectID: string, crashGroupID: string ) => Promise<void>;
    analyticsCrashGroupUpdateSummary: ( projectID: string, crashGroupID: string, userSummary: string ) => Promise<void>;
  }
  